import { useCollection } from '@amzn/awsui-collection-hooks';
import * as React from 'react';
import React__default, { useState, useEffect } from 'react';
import { v4 } from 'uuid';
import Button from '@amzn/awsui-components-react-v3/polaris/button';
import styled from 'styled-components';
import * as awsui from '@amzn/awsui-design-tokens';
import Table from '@amzn/awsui-components-react-v3/polaris/table';
import TextFilter from '@amzn/awsui-components-react-v3/polaris/text-filter';
import Pagination from '@amzn/awsui-components-react-v3/polaris/pagination';
import Box from '@amzn/awsui-components-react-v3/polaris/box';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _taggedTemplateLiteralLoose(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }

  strings.raw = raw;
  return strings;
}

var TreeLineModes;

(function (TreeLineModes) {
  TreeLineModes["MiddleChild"] = "MiddleChild";
  TreeLineModes["LastChild"] = "LastChild";
  TreeLineModes["ChildOfMiddleChild"] = "ChildOfMiddleChild";
  TreeLineModes["ChildOfLastChild"] = "ChildOfLastChild";
  TreeLineModes["Indentation"] = "Indentation";
})(TreeLineModes || (TreeLineModes = {}));

var ExpandableTableNodeStatus;

(function (ExpandableTableNodeStatus) {
  ExpandableTableNodeStatus[ExpandableTableNodeStatus["normal"] = 0] = "normal";
  ExpandableTableNodeStatus[ExpandableTableNodeStatus["loading"] = 1] = "loading";
  ExpandableTableNodeStatus[ExpandableTableNodeStatus["emptyChildren"] = 2] = "emptyChildren";
})(ExpandableTableNodeStatus || (ExpandableTableNodeStatus = {}));

var InternalTreeNode = /*#__PURE__*/function () {
  function InternalTreeNode(params, metadata) {
    this.hasChildren = false;
    this.id = v4();
    this.metadata = {
      prefix: [],
      children: [],
      status: ExpandableTableNodeStatus.normal,
      expanded: false,
      visible: false
    };
    Object.assign(this, params);
    this.metadata = _extends({}, this.metadata, metadata);
  }

  var _proto = InternalTreeNode.prototype;

  _proto.addChild = function addChild(child) {
    this.metadata.children.push(child);
  };

  _proto.removeAllChildren = function removeAllChildren() {
    this.metadata.children = [];
  };

  _proto.getChildren = function getChildren() {
    return this.metadata.children;
  };

  _proto.getParent = function getParent() {
    return this.metadata.parent;
  };

  _proto.toggleExpandCollapse = function toggleExpandCollapse() {
    this.metadata.expanded = !this.metadata.expanded;
  };

  _proto.isExpanded = function isExpanded() {
    return this.metadata.expanded;
  };

  _proto.isVisible = function isVisible() {
    return this.metadata.visible;
  };

  _proto.getMetadata = function getMetadata() {
    return this.metadata;
  };

  _proto.getPrefix = function getPrefix() {
    return this.metadata.prefix;
  };

  _proto.getStatus = function getStatus() {
    return this.metadata.status;
  };

  _proto.setStatus = function setStatus(status) {
    this.metadata.status = status;
  };

  _proto.setVisible = function setVisible(visible) {
    this.metadata.visible = visible;
  };

  _proto.setParentNode = function setParentNode(parentNode) {
    this.metadata.parent = parentNode;
  };

  _proto.buildPrefix = function buildPrefix(lastChild, parentLastChildPath) {
    var parent = this.getParent();

    if (!parent) {
      return;
    }

    var prefix = [TreeLineModes.Indentation, lastChild ? TreeLineModes.LastChild : TreeLineModes.MiddleChild];

    for (var i = parentLastChildPath.length - 1; i >= 1; i -= 1) {
      var isParentLastChild = parentLastChildPath[i];
      var treeLineMode = isParentLastChild ? TreeLineModes.ChildOfLastChild : TreeLineModes.ChildOfMiddleChild;
      prefix.splice(1, 0, treeLineMode);
    }

    this.metadata.prefix = prefix;
  };

  return InternalTreeNode;
}();

var TreeNode = InternalTreeNode;

var TreeUtility = function TreeUtility() {};

TreeUtility.buildTreeNodes = function (items, treeMap, keyPropertyName, parentKeyPropertyName, expandedPropertyName) {
  var staleNodeKeys = new Set(Array.from(treeMap.keys()));
  var treeNodes = items.map(function (item) {
    var key = item[keyPropertyName];
    staleNodeKeys["delete"](key);
    var node = TreeUtility.createNode(item, treeMap, keyPropertyName, parentKeyPropertyName); // Expand the node

    if (expandedPropertyName && item[expandedPropertyName]) {
      TreeUtility.expandRecursivelyTillRoot(node, parentKeyPropertyName);
    }

    return node;
  }).map(function (node) {
    return TreeUtility.prepareNode(node, treeMap, keyPropertyName);
  }).filter(function (node) {
    return typeof node.getParent() === 'undefined';
  });
  TreeUtility.cleanupTree(keyPropertyName, treeMap, staleNodeKeys);
  return treeNodes;
};

TreeUtility.createNode = function (item, treeMap, keyPropertyName, parentKeyPropertyName) {
  var key = item[keyPropertyName];
  var node = treeMap.get(key);

  if (node) {
    // in case exists just updates
    TreeUtility.updateNode(node, item);
  } else {
    node = new TreeNode(item);
  }

  TreeUtility.createOrSetParentNode(node, treeMap, keyPropertyName, parentKeyPropertyName);
  treeMap.set(key, node);
  return node;
};

TreeUtility.createOrSetParentNode = function (node, treeMap, keyPropertyName, parentKeyPropertyName) {
  var parentKey = node[parentKeyPropertyName];

  if (parentKey) {
    var _TreeNode;

    var parentNode = treeMap.get(parentKey) || new TreeNode((_TreeNode = {}, _TreeNode[keyPropertyName] = parentKey, _TreeNode));

    if (parentNode.getChildren().length === 0 || node.getParent() !== parentNode) {
      node.setParentNode(parentNode);
      parentNode.addChild(node);
    }

    treeMap.set(parentKey, parentNode);
  }
};

TreeUtility.prepareNode = function (node, treeMap, keyPropertyName) {
  var key = node[keyPropertyName];
  var isVisible = node.getParent() ? node.getParent().isExpanded() && node.getParent().isVisible() : true;
  node.setVisible(isVisible);
  node.setStatus(node.hasChildren || node.getChildren().length > 0 ? ExpandableTableNodeStatus.normal : ExpandableTableNodeStatus.emptyChildren);
  treeMap.set(key, node);
  return node;
};

TreeUtility.cleanupTree = function (keyPropertyName, treeMap, staleNodeKeys) {
  staleNodeKeys.forEach(function (key) {
    var node = treeMap.get(key);

    if (node) {
      TreeUtility.removeNode(node, keyPropertyName, treeMap);
    }
  });
};

TreeUtility.removeNode = function (node, keyPropertyName, treeMap) {
  var key = node[keyPropertyName];

  if (node.getParent()) {
    var parentChildren = node.getParent().getChildren();
    var childIndex = parentChildren.findIndex(function (child) {
      return child === node;
    });
    parentChildren.splice(childIndex, 1);
    node.setParentNode(undefined);
  }

  node.getChildren().forEach(function (child) {
    return TreeUtility.removeNode(child, keyPropertyName, treeMap);
  });
  node.removeAllChildren();
  treeMap["delete"](key);
};

TreeUtility.buildTreePrefix = function (tree) {
  return tree.map(function (node, index) {
    return TreeUtility.recursiveBuildTreePrefix(node, index, []);
  });
};

TreeUtility.recursiveBuildTreePrefix = function (node, index, parentLastChildPath) {
  var isLastChild = node.getParent() ? node.getParent().getChildren().length - 1 === index : true;
  node.buildPrefix(isLastChild, parentLastChildPath);
  node.getChildren().forEach(function (child, childIndex) {
    return TreeUtility.recursiveBuildTreePrefix(child, childIndex, parentLastChildPath.concat([isLastChild]));
  });
  return node;
};

TreeUtility.flatTree = function (tree) {
  var flattenTree = [];
  TreeUtility.recursiveFlatTree(tree, flattenTree);
  return flattenTree;
};

TreeUtility.recursiveFlatTree = function (tree, flattenTree) {
  tree.forEach(function (node) {
    flattenTree.push(node);

    if (node.getChildren().length) {
      TreeUtility.recursiveFlatTree(node.getChildren(), flattenTree);
    }
  });
};

TreeUtility.expandOrCollapseChildren = function (node, treeMap, keyPropertyName) {
  node.getChildren().forEach(function (child) {
    var key = child[keyPropertyName];
    child.setVisible(node.isExpanded() && node.isVisible());
    treeMap.set(key, child);
    TreeUtility.expandOrCollapseChildren(child, treeMap, keyPropertyName);
  });
};

TreeUtility.expandRecursivelyTillRoot = function (node, parentPropertyName) {
  if (node) {
    if (!node.isExpanded()) {
      node.toggleExpandCollapse();
      node.setVisible(true);
      TreeUtility.expandRecursivelyTillRoot(node.getParent(), parentPropertyName);
    }
  }
};

TreeUtility.updateNode = function (node, newData) {
  Object.keys(newData).forEach(function (prop) {
    node[prop] = newData[prop];
  });
};

TreeUtility.filteringFunction = function (item, filteringText, filteringFields, customFilteringFunction) {
  var filterMatched;

  if (customFilteringFunction) {
    filterMatched = customFilteringFunction(item, filteringText, filteringFields);
  } else {
    var fields = filteringFields || Object.keys(item);
    var lowFilteringText = filteringText.toLowerCase();
    filterMatched = fields.some(function (key) {
      return String(item[key]).toLowerCase().indexOf(lowFilteringText) > -1;
    });
  }

  if (!filterMatched) {
    var childrenFiltered = item.getChildren().map(function (child) {
      return TreeUtility.filteringFunction(child, filteringText, filteringFields);
    }).find(function (found) {
      return found;
    });
    return typeof childrenFiltered !== 'undefined';
  }

  return filterMatched;
};

TreeUtility.sortTree = function (tree, sortState, columnsDefinitions) {
  var sortingColumn = sortState.sortingColumn;

  if (sortingColumn && sortingColumn.sortingField) {
    var columnDefinition = columnsDefinitions.find(function (column) {
      return column.sortingField === sortingColumn.sortingField;
    });
    var direction = sortState.isDescending ? -1 : 1;
    var comparator = columnDefinition.sortingComparator || TreeUtility.defaultComparator(sortState.sortingColumn.sortingField);
    tree.sort(function (a, b) {
      return comparator(a, b) * direction;
    }).forEach(function (node) {
      return TreeUtility.sortTree(node.getChildren(), sortState, columnsDefinitions);
    });
  }
};

TreeUtility.defaultComparator = function (sortingField) {
  return function (row1, row2) {
    var _row1$sortingField, _row2$sortingField;

    // Use empty string as a default value, because it works well to compare with both strings and numbers:
    // Every number can be casted to a string, but not every string can be casted to a meaningful number,
    // sometimes it is NaN.
    var value1 = (_row1$sortingField = row1[sortingField]) != null ? _row1$sortingField : '';
    var value2 = (_row2$sortingField = row2[sortingField]) != null ? _row2$sortingField : '';

    if (typeof value1 === 'string' && typeof value2 === 'string') {
      return value1.localeCompare(value2);
    }

    if (value1 < value2) {
      return -1;
    } // use loose comparison to handle inconsistent data types: https://issues.amazon.com/issues/AWSUI-9232
    // eslint-disable-next-line eqeqeq


    return value1 == value2 ? 0 : 1;
  };
};

var _excluded = ["keyPropertyName", "parentKeyPropertyName", "expandedPropertyName", "columnDefinitions"];
var useTreeCollection = function useTreeCollection(items, props, expanded) {
  var _collectionProps$sort;

  if (expanded === void 0) {
    expanded = false;
  }

  var keyPropertyName = props.keyPropertyName,
      parentKeyPropertyName = props.parentKeyPropertyName,
      expandedPropertyName = props.expandedPropertyName,
      columnDefinitions = props.columnDefinitions,
      collectionProps = _objectWithoutPropertiesLoose(props, _excluded);

  var _useState = useState(new Map()),
      treeMap = _useState[0],
      setTreeMap = _useState[1];

  var _useState2 = useState([]),
      nodes = _useState2[0],
      setNodes = _useState2[1];

  var _useState3 = useState(_extends({}, ((_collectionProps$sort = collectionProps.sorting) == null ? void 0 : _collectionProps$sort.defaultState) || {})),
      sortState = _useState3[0],
      setSortState = _useState3[1];

  var _useState4 = useState(columnDefinitions),
      columnsDefinitions = _useState4[0];

  var _useState5 = useState({}),
      nodesExpanded = _useState5[0],
      addNodesExpanded = _useState5[1];

  useEffect(function () {
    var treeNodes = TreeUtility.buildTreeNodes(items, treeMap, keyPropertyName, parentKeyPropertyName, expandedPropertyName);
    TreeUtility.sortTree(treeNodes, sortState, columnsDefinitions); // only builds prefix after building and sorting the tree

    var tree = TreeUtility.buildTreePrefix(treeNodes);
    setNodes(TreeUtility.flatTree(tree));
  }, [items, keyPropertyName, parentKeyPropertyName, expandedPropertyName, sortState, columnsDefinitions, treeMap]);

  var expandNode = function expandNode(node) {
    if (node) {
      var key = node[keyPropertyName];
      var internalNode = nodes.find(function (n) {
        return n[keyPropertyName] === key;
      });
      internalNode.toggleExpandCollapse();
      TreeUtility.expandOrCollapseChildren(internalNode, treeMap, keyPropertyName);
      treeMap.set(key, internalNode);
      var updatedNodes = nodes.concat([]);
      setNodes(updatedNodes);
      setTreeMap(treeMap);
    }
  };

  var reset = function reset() {
    setNodes([]);
    setTreeMap(new Map());
  };

  var hasPropertyFiltering = !!collectionProps.propertyFiltering;

  var internalCollectionProps = _extends({}, collectionProps, {
    sorting: undefined,
    filtering: !hasPropertyFiltering ? _extends({}, collectionProps.filtering, {
      filteringFunction: function filteringFunction(item, filteringText, filteringFields) {
        var _collectionProps$filt;

        return TreeUtility.filteringFunction(item, filteringText, filteringFields, (_collectionProps$filt = collectionProps.filtering) == null ? void 0 : _collectionProps$filt.filteringFunction);
      }
    }) : undefined
  });

  useEffect(function () {
    if (expanded) {
      var newNodesExpanded = {};
      nodes.forEach(function (node) {
        if (!nodesExpanded[node.id]) {
          var _extends2;

          if (!node.isExpanded()) {
            node.toggleExpandCollapse();
          }

          node.setVisible(true);
          newNodesExpanded = _extends({}, newNodesExpanded, (_extends2 = {}, _extends2[node.id] = true, _extends2));
        }
      });

      if (Object.keys(newNodesExpanded).length > 0) {
        addNodesExpanded(_extends({}, nodesExpanded, newNodesExpanded));
      }
    }
  }, [nodesExpanded, nodes, expanded]);
  var collectionResult = useCollection(nodes, internalCollectionProps);
  var isTextFiltering = collectionResult.filterProps.filteringText.length !== 0;

  var useCollectionResult = _extends({}, collectionResult, {
    items: !hasPropertyFiltering && isTextFiltering ? collectionResult.items : collectionResult.items.filter(function (item) {
      return item.isVisible();
    }),
    collectionProps: _extends({}, collectionResult.collectionProps, {
      sortingColumn: sortState.sortingColumn,
      sortingDescending: sortState.isDescending,
      onSortingChange: function onSortingChange(event) {
        setSortState(event.detail);
        var customOnSortingChange = collectionResult.collectionProps.onSortingChange;

        if (customOnSortingChange) {
          customOnSortingChange(event);
        }
      }
    })
  });

  return _extends({
    expandNode: expandNode,
    reset: reset
  }, useCollectionResult);
};

var _templateObject, _templateObject2, _templateObject3, _templateObject4;
var Wrapper = styled.div(_templateObject || (_templateObject = _taggedTemplateLiteralLoose(["\n  position: relative;\n  margin-top: -1rem;\n  margin-bottom: -1rem;\n  margin-left: -1rem;\n  height: ", "%;\n"])), function (props) {
  return props.height;
});
var LeftPad = styled.div(_templateObject2 || (_templateObject2 = _taggedTemplateLiteralLoose(["\n  display: flex;\n  align-items: center;\n  margin-left: ", "rem;\n"])), function (_ref) {
  var length = _ref.length;
  return length || 0;
});
var EmptySpace = styled.span(_templateObject3 || (_templateObject3 = _taggedTemplateLiteralLoose(["\n  position: relative;\n  min-width: ", "rem;\n  height: ", "rem;\n"])), function (props) {
  return props.width;
}, function (props) {
  return props.height;
});
var ButtonWrapper = styled.div(_templateObject4 || (_templateObject4 = _taggedTemplateLiteralLoose(["\n  align-self: flex-start;\n"])));

var TABLE_ROW_HEIGHT_PERCENT$1 = 100;
var WRAPPER_EXTRA_HEIGHT_PERCENT$1 = 25;
var WRAPPER_VERTICAL_MARGIN_DIFF = 0.5;
var POLARIS_BUTTON_PADDING_DIFF = 0.3;
var ICON_BUTTON_WIDTH_PERCENT = 100;
var ICON_BUTTON_MIDPOINT_X = ICON_BUTTON_WIDTH_PERCENT / 2;
var ICON_BUTTON_MIDPOINT_Y = TABLE_ROW_HEIGHT_PERCENT$1 / 2;
var SVG_WIDTH_IN_REM = 2.0;
var POLARIS_OPEN_SOURCE_TOP_MARGIN_DIFF = 0.2;
var POLARIS_OPEN_SOURCE_SVG_WIDTH_IN_REM = 1.7;
var Theme;

(function (Theme) {
  Theme["POLARIS_INTERNAL"] = "INTERNAL";
  Theme["POLARIS_OPEN_SOURCE"] = "OPEN_SOURCE";
})(Theme || (Theme = {}));

var Dir;

(function (Dir) {
  Dir[Dir["Top"] = 0] = "Top";
  Dir[Dir["Bottom"] = 1] = "Bottom";
  Dir[Dir["LittleBottom"] = 2] = "LittleBottom";
  Dir[Dir["Right"] = 3] = "Right";
})(Dir || (Dir = {}));

var getLines = function getLines(directions) {
  return directions.map(function (dir, index) {
    var y1 = TABLE_ROW_HEIGHT_PERCENT$1;
    var y2 = ICON_BUTTON_MIDPOINT_Y;

    if (dir === Dir.Top) {
      y1 = 0;
    }

    if (dir === Dir.Right) {
      y1 = ICON_BUTTON_MIDPOINT_Y;
    }

    if (dir === Dir.LittleBottom) {
      y2 = 0;
    }

    var x2 = ICON_BUTTON_MIDPOINT_X;

    if (dir === Dir.Right) {
      x2 = ICON_BUTTON_WIDTH_PERCENT;
    }

    return React__default.createElement("line", {
      style: {
        stroke: awsui.colorBorderDividerDefault,
        strokeWidth: 2,
        vectorEffect: 'non-scaling-stroke'
      },
      key: "Line" + index,
      x1: ICON_BUTTON_MIDPOINT_X + "%",
      x2: x2 + "%",
      y1: y1 + "%",
      y2: y2 + "%"
    });
  });
};

var getTopMargin = function getTopMargin(isLittleBottom, theme) {
  var topMarginPolarisInternal = isLittleBottom ? 1.5 + WRAPPER_VERTICAL_MARGIN_DIFF : "-" + WRAPPER_VERTICAL_MARGIN_DIFF;
  var topMarginPolarisOpenSource = isLittleBottom ? 1.3 + POLARIS_OPEN_SOURCE_TOP_MARGIN_DIFF : "-" + POLARIS_OPEN_SOURCE_TOP_MARGIN_DIFF;
  return theme === Theme.POLARIS_INTERNAL ? topMarginPolarisInternal : topMarginPolarisOpenSource;
};

var getHeight = function getHeight(width, hasRightLine, theme) {
  var heightPolarisInternal = hasRightLine ? width * 2 + "rem" : TABLE_ROW_HEIGHT_PERCENT$1 + WRAPPER_EXTRA_HEIGHT_PERCENT$1 + "%";
  var heightPolarisOpenSource = hasRightLine ? width + 0.7 + "rem" : TABLE_ROW_HEIGHT_PERCENT$1 + WRAPPER_EXTRA_HEIGHT_PERCENT$1 + "%";
  return theme === Theme.POLARIS_INTERNAL ? heightPolarisInternal : heightPolarisOpenSource;
};

var createLinesSvg = function createLinesSvg(directions, theme, index) {
  var width = theme === Theme.POLARIS_INTERNAL ? SVG_WIDTH_IN_REM : POLARIS_OPEN_SOURCE_SVG_WIDTH_IN_REM;
  var leftPos = (index - 1) * 2;
  var rightPos = leftPos + width;
  var rightLine = directions.find(function (dir) {
    return dir === Dir.Right;
  });
  var lines = [directions.filter(function (dir) {
    return dir !== Dir.Right;
  })];

  if (rightLine) {
    lines.push([rightLine]);
  }

  return lines.map(function (lineDirections) {
    var isLittleBottom = lineDirections.length === 1 && lineDirections[0] === Dir.LittleBottom;
    var isRightLineOnly = lineDirections.length === 1 && lineDirections[0] === Dir.Right;
    var isTopWithRightLine = lineDirections.length === 1 && lineDirections[0] === Dir.Top && !!rightLine;
    var paddingLeft = theme === Theme.POLARIS_INTERNAL ? POLARIS_BUTTON_PADDING_DIFF + "rem" : '';
    var topMargin = getTopMargin(isLittleBottom, theme);
    var height = getHeight(width, isRightLineOnly || isTopWithRightLine, theme);
    var viewBox = isRightLineOnly || isTopWithRightLine ? "0 0 " + width + " " + width * 2 : "0 0 " + width + " " + TABLE_ROW_HEIGHT_PERCENT$1;
    return React__default.createElement("svg", {
      key: "" + leftPos + lineDirections.join('_') + index,
      className: "related-table-tree-line",
      style: {
        margin: topMargin + "rem 0 0 0",
        paddingLeft: paddingLeft,
        position: 'absolute',
        top: 0,
        left: leftPos + "rem",
        right: rightPos + "rem",
        bottom: 0,
        width: width + "rem",
        height: height
      },
      viewBox: viewBox,
      preserveAspectRatio: "none"
    }, getLines(lineDirections));
  });
};

function createPrefixLines(node, theme, alwaysExpanded) {
  if (alwaysExpanded === void 0) {
    alwaysExpanded = false;
  }

  var prefixSequence = [];
  node.getPrefix().forEach(function (prefix, index) {
    switch (prefix) {
      case TreeLineModes.Indentation: // Fallthrough, used for external readability

      case TreeLineModes.ChildOfLastChild:
        break;

      case TreeLineModes.LastChild:
        prefixSequence.splice.apply(prefixSequence, [index, 0].concat(createLinesSvg([Dir.Top, Dir.Right], theme, index)));
        break;

      case TreeLineModes.ChildOfMiddleChild:
        prefixSequence.splice.apply(prefixSequence, [index, 0].concat(createLinesSvg([Dir.Top, Dir.Bottom], theme, index)));
        break;

      case TreeLineModes.MiddleChild:
        prefixSequence.splice.apply(prefixSequence, [index, 0].concat(createLinesSvg([Dir.Top, Dir.Bottom, Dir.Right], theme, index)));
        break;

      default:
        prefixSequence.push(React__default.createElement("span", {
          key: "empty"
        }));
    }
  });

  if ((node.isExpanded() || alwaysExpanded) && node.getChildren().length > 0) {
    prefixSequence.splice.apply(prefixSequence, [prefixSequence.length - 1, 0].concat(createLinesSvg([Dir.LittleBottom], theme, node.getPrefix().length || 1)));
  }

  return prefixSequence;
}

var THEME = 'INTERNAL';

var TABLE_ROW_HEIGHT_PERCENT = 100;
var WRAPPER_EXTRA_HEIGHT_PERCENT = 25;
var MARGIN_LEFT_REM_MULTIPLICATOR = 2;

var noAction = function noAction() {};

var theme = THEME;
var emptySpaceHeight = theme === Theme.POLARIS_OPEN_SOURCE ? 2 : 3;
var emptySpaceWidth = theme === Theme.POLARIS_OPEN_SOURCE ? 0.4 : 0.5;

function createToggleButton(props) {
  var _props$buttonI18nLabe, _props$buttonI18nLabe2;

  var node = props.node,
      onClick = props.onClick,
      alwaysExpanded = props.alwaysExpanded;
  var icon = node.isExpanded() || alwaysExpanded ? 'treeview-collapse' : 'treeview-expand';
  var i18nLabel = node.isExpanded() || alwaysExpanded ? (_props$buttonI18nLabe = props.buttonI18nLabels) == null ? void 0 : _props$buttonI18nLabe.collapse : (_props$buttonI18nLabe2 = props.buttonI18nLabels) == null ? void 0 : _props$buttonI18nLabe2.expand;
  return node.getChildren().length > 0 || node.hasChildren ? React__default.createElement(ButtonWrapper, null, React__default.createElement(Button, {
    disabled: node.getStatus() !== ExpandableTableNodeStatus.normal,
    variant: "icon",
    ariaLabel: i18nLabel,
    iconName: icon,
    onClick: alwaysExpanded ? noAction : onClick
  })) : React__default.createElement(EmptySpace, {
    height: emptySpaceHeight,
    width: emptySpaceWidth
  });
}

var ButtonWithTreeLines = React__default.memo(function ButtonWithTreeLinesComp(props) {
  var node = props.node,
      content = props.content,
      alwaysExpanded = props.alwaysExpanded;
  var leftPadLength = node.getPrefix().length ? MARGIN_LEFT_REM_MULTIPLICATOR * (node.getPrefix().length - 1) : 0;
  return React__default.createElement(Wrapper, {
    height: TABLE_ROW_HEIGHT_PERCENT + WRAPPER_EXTRA_HEIGHT_PERCENT
  }, React__default.createElement(LeftPad, {
    length: leftPadLength
  }, createPrefixLines(node, theme, alwaysExpanded), createToggleButton(props), content));
});

function RelatedTable(props) {
  var columnDefinitions = props.columnDefinitions,
      items = props.items,
      expandChildren = props.expandChildren,
      _props$expandColumnPo = props.expandColumnPosition,
      expandColumnPosition = _props$expandColumnPo === void 0 ? 1 : _props$expandColumnPo,
      _props$filteringText = props.filteringText,
      filteringText = _props$filteringText === void 0 ? '' : _props$filteringText,
      buttonI18nLabels = props.buttonI18nLabels;
  var isFiltering = filteringText !== '';
  var zeroBasedColumnPos = expandColumnPosition - 1;
  var columns = [].concat(columnDefinitions);
  var columnToExpand = columns[zeroBasedColumnPos];
  columns[zeroBasedColumnPos] = _extends({}, columnToExpand, {
    cell: function cell(node) {
      var cell = columnToExpand == null ? void 0 : columnToExpand.cell(node);
      return React__default.createElement(ButtonWithTreeLines, {
        alwaysExpanded: isFiltering,
        node: node,
        buttonI18nLabels: buttonI18nLabels,
        content: cell,
        onClick: function onClick() {
          expandChildren(node);
        }
      });
    }
  });
  return React__default.createElement(Table, _extends({}, props, {
    columnDefinitions: columns,
    items: items || []
  }));
}

/**
 * Empty state for tables component.
 *
 * https://polaris.a2z.com/patterns/design_patterns/empty_states/
 */

var EmptyState = function EmptyState(_ref) {
  var header = _ref.header,
      description = _ref.description;
  return React.createElement(Box, {
    textAlign: "center",
    color: "inherit"
  }, header && React.createElement(Box, {
    variant: "strong",
    textAlign: "center",
    color: "inherit"
  }, header), description && React.createElement(Box, {
    variant: "p",
    padding: {
      bottom: 's'
    },
    color: "inherit"
  }, description));
};

var withUseTreeCollection = function withUseTreeCollection(RelatedTableComp) {
  return React.forwardRef(function (props, ref) {
    var items = props.items,
        columnDefinitions = props.columnDefinitions,
        collectionOptions = props.collectionOptions,
        _expandChildren = props.expandChildren,
        empty = props.empty,
        filter = props.filter,
        selectedItems = props.selectedItems,
        _onSortingChange = props.onSortingChange,
        onSelectionChange = props.onSelectionChange,
        onReady = props.onReady,
        expanded = props.expanded;

    var _useTreeCollection = useTreeCollection(items, _extends({}, collectionOptions, {
      columnDefinitions: columnDefinitions
    }), expanded),
        nodes = _useTreeCollection.items,
        collectionProps = _useTreeCollection.collectionProps,
        filterProps = _useTreeCollection.filterProps,
        paginationProps = _useTreeCollection.paginationProps,
        expandNode = _useTreeCollection.expandNode,
        actions = _useTreeCollection.actions,
        reset = _useTreeCollection.reset;

    var renderEmpty = function renderEmpty() {
      if (empty) {
        return React.createElement(EmptyState, _extends({}, empty));
      }

      return null;
    };

    var renderFilter = function renderFilter() {
      if (filter) {
        return React.createElement(TextFilter, _extends({}, filter, filterProps));
      }

      return null;
    };

    var renderPagination = function renderPagination() {
      if (collectionOptions.pagination) {
        return React.createElement(Pagination, _extends({}, collectionOptions.pagination, paginationProps));
      }

      return null;
    };

    if (onReady) {
      onReady(_extends({}, actions, {
        reset: reset
      }));
    }

    return React.createElement(RelatedTableComp, _extends({}, props, filterProps, collectionProps, {
      items: nodes || [],
      columnDefinitions: columnDefinitions,
      trackBy: collectionOptions.keyPropertyName,
      expandChildren: function expandChildren(node) {
        expandNode(node);

        _expandChildren(node);
      },
      empty: renderEmpty(),
      selectedItems: selectedItems,
      onSelectionChange: onSelectionChange,
      onSortingChange: function onSortingChange(event) {
        if (_onSortingChange) {
          _onSortingChange(event);
        }

        if (collectionProps.onSortingChange) {
          collectionProps.onSortingChange(event);
        }
      },
      filter: renderFilter(),
      pagination: renderPagination(),
      ref: ref
    }));
  });
};

export { ButtonWithTreeLines, ButtonWrapper, EmptySpace, ExpandableTableNodeStatus, LeftPad, MARGIN_LEFT_REM_MULTIPLICATOR, RelatedTable, TreeLineModes, TreeNode, TreeUtility, Wrapper, useTreeCollection, withUseTreeCollection };
//# sourceMappingURL=index.module.js.map
